import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Star, Check, X as XIcon, Image as ImageIcon, Loader2, Crown, Rocket } from 'lucide-react';
import { toast } from 'sonner';

const UPGRADE_EVENT = 'show-upgrade-modal';

/**
 * Imperative trigger so non-React code (e.g. axios interceptor) can open the modal.
 * Usage:  window.dispatchEvent(new CustomEvent('show-upgrade-modal', {detail: {reason: '...'}}))
 */
export const openUpgradeModal = (reason) => {
  window.dispatchEvent(new CustomEvent(UPGRADE_EVENT, { detail: { reason } }));
};

const TIER_ICON = { starter: Rocket, growth: Star, pro: Crown };

/**
 * Friendly upgrade modal that auto-appears when the backend returns 402
 * (image quota / posts quota / trial expired). Renders the 3 paid tiers
 * side-by-side with image limits highlighted, and one-click launches Stripe
 * Checkout via /api/subscription/checkout.
 */
export const UpgradeModal = () => {
  const { api, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [tiers, setTiers] = useState([]);
  const [loadingTiers, setLoadingTiers] = useState(false);
  const [busyTier, setBusyTier] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      setReason(e?.detail?.reason || '');
      setOpen(true);
    };
    window.addEventListener(UPGRADE_EVENT, handler);
    return () => window.removeEventListener(UPGRADE_EVENT, handler);
  }, []);

  // Lazy-load tiers the first time the modal opens — no need to fetch on every page render.
  useEffect(() => {
    if (!open || tiers.length > 0) return;
    setLoadingTiers(true);
    api
      .get('/subscription/tiers')
      .then((r) => {
        const all = r.data?.tiers || [];
        // Show paid, non-trial tiers in marketing order
        const order = ['starter', 'growth', 'pro'];
        setTiers(
          all
            .filter((t) => order.includes(t.id))
            .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
        );
      })
      .catch(() => { /* fail-soft, modal still renders header/CTA */ })
      .finally(() => setLoadingTiers(false));
  }, [open, api, tiers.length]);

  const handleUpgrade = async (tierId) => {
    if (!user) {
      // Not logged in — send to register, then they can checkout from there.
      window.location.href = `/register?plan=${tierId}`;
      return;
    }
    setBusyTier(tierId);
    try {
      const res = await api.post('/subscription/checkout', {
        tier_id: tierId,
        origin_url: window.location.origin,
      });
      const url = res.data?.checkout_url;
      if (!url) throw new Error('No checkout URL returned');
      window.location.href = url; // hand off to Stripe
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Could not start checkout. Please try again.'
      );
      setBusyTier(null);
    }
  };

  const headline = useMemo(() => {
    if (!reason) return "You've hit your plan's limit";
    // Reason from backend is already user-friendly; trim any trailing period for display.
    return reason.replace(/\.$/, '');
  }, [reason]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-3xl p-0 gap-0 overflow-hidden"
        data-testid="upgrade-modal"
      >
        <DialogHeader className="bg-gradient-to-br from-indigo-600 via-violet-600 to-pink-600 text-white px-6 py-5 relative">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="absolute top-3 right-3 w-7 h-7 rounded-md bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            aria-label="Close"
            data-testid="upgrade-modal-close"
          >
            <XIcon className="w-3.5 h-3.5" />
          </button>
          <div className="flex items-center gap-2">
            <ImageIcon className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase tracking-wider opacity-90">
              Upgrade required
            </span>
          </div>
          <DialogTitle className="text-2xl sm:text-3xl font-bold leading-tight pr-8 mt-1">
            {headline}.
          </DialogTitle>
          <p className="text-white/80 text-sm mt-1.5 max-w-2xl">
            Pick a plan to keep generating AI images and unlock the rest of the workspace.
            All plans include cancel-anytime billing — no contracts.
          </p>
        </DialogHeader>

        <div className="p-5 sm:p-6 bg-zinc-50">
          {loadingTiers ? (
            <div className="flex items-center justify-center py-12 text-zinc-500 text-sm">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Loading plans…
            </div>
          ) : (
            <div className="grid sm:grid-cols-3 gap-3">
              {tiers.map((tier) => {
                const Icon = TIER_ICON[tier.id] || Rocket;
                const popular = !!tier.is_popular || tier.id === 'growth';
                return (
                  <div
                    key={tier.id}
                    className={`relative rounded-xl border bg-white p-4 flex flex-col ${
                      popular
                        ? 'border-indigo-500 ring-2 ring-indigo-100 shadow-md'
                        : 'border-zinc-200'
                    }`}
                    data-testid={`upgrade-tier-${tier.id}`}
                  >
                    {popular && (
                      <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-indigo-600 hover:bg-indigo-600 text-white text-[10px] font-semibold px-2 py-0.5">
                        Most Popular
                      </Badge>
                    )}
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className={`w-7 h-7 rounded-md flex items-center justify-center ${
                          popular ? 'bg-indigo-600 text-white' : 'bg-zinc-100 text-zinc-700'
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                      </div>
                      <h3 className="font-bold text-zinc-900">{tier.name}</h3>
                    </div>
                    <div className="mb-3">
                      <span className="text-2xl sm:text-3xl font-bold text-zinc-900">
                        ${Math.round(tier.price)}
                      </span>
                      <span className="text-zinc-500 text-sm ml-0.5">/mo</span>
                    </div>
                    <div className="rounded-md bg-indigo-50 px-2.5 py-1.5 mb-3 flex items-center gap-1.5">
                      <ImageIcon className="w-3.5 h-3.5 text-indigo-600 flex-shrink-0" />
                      <span className="text-xs font-semibold text-indigo-700 leading-tight">
                        {tier.ai_images_per_month?.toLocaleString() || tier.posts_per_month}
                        {' '}AI images / month
                      </span>
                    </div>
                    <ul className="space-y-1.5 mb-4 flex-1">
                      {(tier.features || []).slice(0, 4).map((f, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-xs text-zinc-700 leading-snug">
                          <Check className="w-3 h-3 mt-0.5 text-emerald-600 flex-shrink-0" />
                          <span className="line-clamp-2">{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      onClick={() => handleUpgrade(tier.id)}
                      disabled={busyTier === tier.id}
                      className={`w-full h-9 text-xs font-semibold ${
                        popular
                          ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                          : 'bg-zinc-900 hover:bg-zinc-800 text-white'
                      }`}
                      data-testid={`upgrade-tier-${tier.id}-btn`}
                    >
                      {busyTier === tier.id ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                          Redirecting…
                        </>
                      ) : (
                        <>Upgrade to {tier.name}</>
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-[11px] text-zinc-500 text-center mt-4">
            Cancel anytime · Secure checkout via Stripe · Instant access on upgrade
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeModal;
